/**
 * Shared toast helpers around Mantine `notifications.show`.
 *
 * Every admin-side mutation has the same shape: try the API call,
 * catch errors, surface them as a red toast. Without a helper, every
 * site repeats the `err instanceof ApiError ? ... : String(err)`
 * boilerplate. The repetition has two failure modes in practice:
 *
 * - One site forgets to type-narrow and renders `[object Object]`.
 * - Drift on the success-toast colour / icon / autoClose between
 *   sites so the UX feels inconsistent.
 *
 * `toastApiError`, `toastSuccess`, and `toastWarning` are the only
 * three shapes admin code should reach for. Anything bespoke
 * (auto-close: false for chain-broken alerts, custom icons) still
 * uses `notifications.show` directly.
 */

import { notifications } from "@mantine/notifications";
import { ApiError } from "@/shared/api/apiFetch";

interface ApiErrorToastOpts {
  /** Optional override; defaults to the `ApiError.message` or a
   *  generic "Action failed" for non-ApiError throws. */
  message?: string;
  /** Override the auto-close duration. Toasts that require operator
   *  action (e.g. a broken audit chain) pass `false` to keep the
   *  toast pinned until dismissed. */
  autoClose?: number | false;
}

/**
 * Render an error from a `try { await api(...) } catch (err) { ... }`
 * block as a red toast. Surfaces the API's `error.code` as a small
 * tag when one is available so the operator can grep logs for it.
 */
export function toastApiError(
  title: string,
  err: unknown,
  opts: ApiErrorToastOpts = {},
): void {
  let message: string;
  if (opts.message !== undefined) {
    message = opts.message;
  } else if (err instanceof ApiError) {
    message = `${err.message} (${err.code})`;
  } else if (err instanceof Error) {
    message = err.message;
  } else {
    message = String(err);
  }
  notifications.show({
    title,
    message,
    color: "red",
    autoClose: opts.autoClose,
  });
}

/**
 * Green confirmation toast. Use for state-change actions that
 * succeeded (provision, deactivate, role assign).
 */
export function toastSuccess(title: string, message: string): void {
  notifications.show({ title, message, color: "green" });
}

/**
 * Red toast for failures that DIDN'T raise — the API returned a body
 * that the caller wants to surface as a failure (e.g. audit-chain
 * verify came back `ok=false`). For raised exceptions use
 * `toastApiError` so the API code is included.
 *
 * `autoClose: false` pins the toast until the operator dismisses it;
 * use it for results that demand attention (broken chain, KEK
 * destruction confirmation).
 */
export function toastError(
  title: string,
  message: string,
  opts: { autoClose?: number | false } = {},
): void {
  notifications.show({
    title,
    message,
    color: "red",
    autoClose: opts.autoClose,
  });
}

/**
 * Amber toast for actions that completed but warrant attention
 * (deactivate, "rotation skipped N erased subjects"). Distinct from
 * success because operators visually scan for it.
 */
export function toastWarning(title: string, message: string): void {
  notifications.show({ title, message, color: "yellow" });
}
